import { useQuery } from '@tanstack/react-query';
import {
  Calendar,
  Clock,
  Users,
  CalendarCheck,
  Settings,
  Bell,
  FileText,
  TrendingUp,
} from 'lucide-react';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';

const supabase = getSupabaseAdminClient();

// Stat Card Component
interface StatCardProps {
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  value: string | number;
  label: string;
  subtext?: string;
}

function StatCard({ icon: Icon, iconBg, iconColor, value, label, subtext }: StatCardProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-6 h-6 ${iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-2xl font-bold text-slate-900">{value}</div>
          <div className="text-sm text-slate-600">{label}</div>
          {subtext && <div className="text-xs text-slate-400 mt-1">{subtext}</div>}
        </div>
      </div>
    </div>
  );
}

// Section Header Component
interface SectionHeaderProps {
  icon: React.ElementType;
  title: string;
  description?: string;
}

function SectionHeader({ icon: Icon, title, description }: SectionHeaderProps) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
        <Icon className="w-5 h-5 text-slate-600" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        {description && <p className="text-sm text-slate-500">{description}</p>}
      </div>
    </div>
  );
}

// Appointment type labels
const appointmentTypeLabels: Record<string, string> = {
  checkup: 'Vorsorgeuntersuchung',
  ultrasound: 'Ultraschall',
  doctor: 'Arzttermin',
  personal: 'Persönlich',
  other: 'Sonstiges',
};

export function CalendarAdminPage() {
  // Fetch appointment statistics
  const { data: stats } = useQuery({
    queryKey: ['calendar-admin-stats'],
    queryFn: async () => {
      const [totalResult, byTypeResult, usersWithAppointmentsResult, upcomingResult] = await Promise.all([
        // Total appointments
        supabase.from('user_appointments').select('id', { count: 'exact', head: true }),
        // By type
        supabase.from('user_appointments').select('appointment_type'),
        // Users with appointments
        supabase.from('user_appointments').select('user_id'),
        // Upcoming (next 7 days)
        supabase
          .from('user_appointments')
          .select('id', { count: 'exact', head: true })
          .gte('appointment_date', new Date().toISOString().split('T')[0])
          .lte('appointment_date', new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]),
      ]);

      // Count by type
      const typeCounts: Record<string, number> = {};
      (byTypeResult.data as { appointment_type: string }[] | null)?.forEach((item) => {
        typeCounts[item.appointment_type] = (typeCounts[item.appointment_type] || 0) + 1;
      });

      // Unique users
      const uniqueUsers = new Set((usersWithAppointmentsResult.data as { user_id: string }[] | null)?.map((u) => u.user_id));

      return {
        total: totalResult.count || 0,
        byType: typeCounts,
        uniqueUsers: uniqueUsers.size,
        upcoming: upcomingResult.count || 0,
      };
    },
  });

  // Appointment type for recent appointments
  interface Appointment {
    id: string;
    title: string;
    appointment_type: string;
    appointment_date: string;
    appointment_time: string | null;
    created_at: string;
    user_id: string;
  }

  // Fetch recent appointments
  const { data: recentAppointments } = useQuery({
    queryKey: ['calendar-admin-recent'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_appointments')
        .select(`
          id,
          title,
          appointment_type,
          appointment_date,
          appointment_time,
          created_at,
          user_id
        `)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      return data as Appointment[];
    },
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatTime = (timeString: string | null) => {
    if (!timeString) return '-';
    return timeString.substring(0, 5);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
            <Calendar className="w-7 h-7 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Kalender</h1>
            <p className="text-slate-500">Übersicht und Einstellungen für den Terminkalender</p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={CalendarCheck}
          iconBg="bg-blue-100"
          iconColor="text-blue-600"
          value={stats?.total || 0}
          label="Termine gesamt"
        />
        <StatCard
          icon={Users}
          iconBg="bg-teal-100"
          iconColor="text-teal-600"
          value={stats?.uniqueUsers || 0}
          label="Nutzer mit Terminen"
        />
        <StatCard
          icon={Clock}
          iconBg="bg-amber-100"
          iconColor="text-amber-600"
          value={stats?.upcoming || 0}
          label="Nächste 7 Tage"
        />
        <StatCard
          icon={TrendingUp}
          iconBg="bg-emerald-100"
          iconColor="text-emerald-600"
          value={stats?.total && stats?.uniqueUsers ? (stats.total / stats.uniqueUsers).toFixed(1) : '0'}
          label="Ø Termine pro Nutzer"
        />
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Termine nach Typ */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <SectionHeader
            icon={FileText}
            title="Termine nach Typ"
            description="Verteilung der Terminarten"
          />
          <div className="space-y-3">
            {Object.entries(appointmentTypeLabels).map(([type, label]) => {
              const count = stats?.byType[type] || 0;
              const percentage = stats?.total ? Math.round((count / stats.total) * 100) : 0;
              return (
                <div key={type} className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-slate-700">{label}</span>
                      <span className="text-sm text-slate-500">{count}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Einstellungen */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <SectionHeader
            icon={Settings}
            title="Einstellungen"
            description="Kalender-Konfiguration"
          />
          <div className="space-y-4">
            {/* Erinnerungen */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <Bell className="w-5 h-5 text-slate-600" />
                <span className="font-medium text-slate-900">Standard-Erinnerungen</span>
              </div>
              <p className="text-sm text-slate-500 mb-3">
                Voreinstellung für neue Termine
              </p>
              <select className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
                <option value="1h">1 Stunde vorher</option>
                <option value="2h">2 Stunden vorher</option>
                <option value="1d">1 Tag vorher</option>
                <option value="2d">2 Tage vorher</option>
              </select>
            </div>

            {/* Terminassistent */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <CalendarCheck className="w-5 h-5 text-slate-600" />
                  <span className="font-medium text-slate-900">Terminassistent</span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500"></div>
                </label>
              </div>
              <p className="text-sm text-slate-500">
                Zeigt den Setup-Wizard bei neuen Nutzern
              </p>
            </div>

            {/* Info Box */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm text-blue-700">
                <strong>Hinweis:</strong> Terminvorlagen (Vorsorgeuntersuchungen, Ultraschall) werden
                derzeit im Code definiert. Eine Verwaltung über das Admin-Panel ist für eine
                spätere Version geplant.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Appointments */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <SectionHeader
          icon={Clock}
          title="Zuletzt erstellte Termine"
          description="Die 10 neuesten Termine"
        />
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Titel</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Typ</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Datum</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Uhrzeit</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Erstellt am</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {recentAppointments?.map((appointment) => (
                <tr key={appointment.id} className="hover:bg-slate-50">
                  <td className="py-3 px-4">
                    <span className="text-sm font-medium text-slate-900">{appointment.title}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded-full">
                      {appointmentTypeLabels[appointment.appointment_type] || appointment.appointment_type}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-slate-600">{formatDate(appointment.appointment_date)}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-slate-600">{formatTime(appointment.appointment_time)}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-slate-500">{formatDate(appointment.created_at)}</span>
                  </td>
                </tr>
              ))}
              {(!recentAppointments || recentAppointments.length === 0) && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-slate-500">
                    Keine Termine vorhanden
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
